import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { useNewOrderFlow } from "@/hooks/useNewOrderFlow";
import { isDeployedTestEnvironment } from "@/lib/config";
import { HOME_TILES } from "@/pages/homeTiles";

export default function HomePage() {
  const navigate = useNavigate();
  const { canAccessFeature } = useFeatureAccess();
  const { startNewOrder } = useNewOrderFlow();
  const visibleTiles = HOME_TILES.filter((tile) =>
    canAccessFeature(tile.feature),
  );

  const handleTileClick = (href: string) => {
    if (href === "/commandes?create=true") {
      startNewOrder();
    } else {
      navigate(href);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-8 2xl:px-12 py-8 sm:py-12 lg:py-8 xl:py-8">
        <div className="mb-8 sm:mb-12 lg:mb-8 text-center space-y-3">
          {isDeployedTestEnvironment() && (
            <div className="flex justify-center">
              <Badge
                variant="outline"
                className="border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-400 px-4 py-1.5 text-sm font-semibold"
              >
                Environnement test
              </Badge>
            </div>
          )}
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6
                        gap-5 sm:gap-6 lg:gap-5 xl:gap-5"
        >
          {visibleTiles.map((tile) => (
            <button
              key={tile.href}
              onClick={() => handleTileClick(tile.href)}
              className="group relative bg-white rounded-xl lg:rounded-xl
                        border-2 border-gray-200 hover:border-blue-400
                        p-5 sm:p-6 lg:p-5 xl:p-5
                        hover:shadow-xl hover:-translate-y-1 active:scale-98
                        transition-all duration-300 ease-out
                        min-h-[160px] sm:min-h-[180px] lg:min-h-[180px] xl:min-h-[180px]
                        flex flex-col justify-between text-left
                        overflow-hidden"
            >
              <div
                className={`w-14 h-14 sm:w-16 sm:h-16 lg:w-14 lg:h-14 xl:w-14 xl:h-14
                              ${tile.color} rounded-xl
                              flex items-center justify-center mb-4 lg:mb-4
                              group-hover:scale-110 transition-transform duration-300
                              shadow-lg border border-white/20`}
              >
                <tile.icon className="w-7 h-7 sm:w-8 sm:h-8 lg:w-7 lg:h-7 xl:w-7 xl:h-7 text-white" />
              </div>

              <div className="space-y-1.5 lg:space-y-1.5">
                <h3
                  className="text-base sm:text-lg lg:text-base xl:text-base
                              font-bold text-gray-900 group-hover:text-blue-600
                              transition-colors duration-300 leading-tight"
                >
                  {tile.title}
                </h3>
                <p
                  className="text-xs sm:text-sm lg:text-xs xl:text-xs
                              text-gray-600 leading-relaxed"
                >
                  {tile.description}
                </p>
              </div>

              <div
                className="absolute top-3 right-3 lg:top-3 lg:right-3 xl:top-3 xl:right-3
                              opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0
                              transition-all duration-300 ease-out"
              >
                <div
                  className="w-7 h-7 sm:w-8 sm:h-8 lg:w-7 lg:h-7 xl:w-7 xl:h-7
                                bg-blue-500 rounded-full flex items-center justify-center shadow-xl"
                >
                  <span className="text-white text-sm lg:text-sm xl:text-sm flex items-center justify-center h-full w-full">
                    →
                  </span>
                </div>
              </div>

              <div
                className="absolute inset-0 bg-gradient-to-t from-blue-500/5 to-transparent
                              opacity-0 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none rounded-xl"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
